import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useQueryClient } from '@tanstack/react-query';
import PageTransition from '../components/PageTransition';
import Toast, { ToastType } from '../components/Toast';
import ConfirmationDialog from '../components/ConfirmationDialog';
import ImageViewerModal from '../components/ImageViewerModal';
import AdditionalNotesModal from '../components/AdditionalNotesModal';
import { getServiceRequestDetail, deleteServiceRequest, getServiceRequestSpareParts } from '../services/serviceRequestService';
import { getNotesData, getRecordFiles } from '../services/api';
import { EtkinlikTipi, IslemAdi } from '../constants/status';
import { API_BASE_URL } from '../constants/api';
import { useUser } from '../contexts/UserContext';
import { useMenu } from '../contexts/MenuContext';
import ProductImage from '../components/ProductImage';
import { notifyAccountCacheDelete } from '../utils/accountCacheUtils';
import { formatPhoneNumber, isValidTurkishMobile } from '../utils/phoneUtils';

export default function ServiceDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { userData } = useUser();
  const { isMenuButtonVisible, isSidebarOpen, isMobile } = useMenu();
  const queryClient = useQueryClient();
  
  // Sabitler
  const VIDEO_PROCESSING_MESSAGE = 'Video hazırlanıyor. Lütfen bekleyin...';
  
  // State değişkenleri
  const [serviceDetail, setServiceDetail] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [spareParts, setSpareParts] = useState<any[]>([]);
  const [serviceNotes, setServiceNotes] = useState<any[]>([]);
  const [recordFiles, setRecordFiles] = useState<any[]>([]);
  
  // Scroll yönünü takip etmek için state ve ref
  const [showDeleteButton, setShowDeleteButton] = useState(true);
  const [showReviseButton, setShowReviseButton] = useState(true);
  const [showBackButton, setShowBackButton] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const lastScrollTop = useRef(0);
  
  // Toast state
  const [toast, setToast] = useState({ isVisible: false, message: '', type: 'info' as ToastType });
  
  // Silme onay dialogu state
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Görsel görüntüleyici state
  const [isImageViewerOpen, setIsImageViewerOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Ek notlar state (Notlar store'undan)
  const [additionalNotes, setAdditionalNotes] = useState<any[]>([]);
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);

  // Video işleme durumu state (index bazlı)
  const [videoProcessingStatus, setVideoProcessingStatus] = useState<{ [key: number]: boolean }>({});
  const videoRetryTimers = useRef<{ [key: number]: NodeJS.Timeout }>({});
  const videoRefs = useRef<{ [key: number]: HTMLVideoElement | null }>({});
  

  // Toast functions
  const showToast = (message: string, type: ToastType = 'info') => {
    setToast({ isVisible: true, message, type });
  };

  const hideToast = () => {
    setToast({ ...toast, isVisible: false });
  };


  // Tam ekran video oynatma fonksiyonu
  const playVideoFullscreen = async (file: any, videoIndex: number) => {
    // Video işleniyorsa bekle
    if (videoProcessingStatus[videoIndex]) {
      showToast(VIDEO_PROCESSING_MESSAGE, 'info');
      return;
    }

    // Tam ekran video elementi oluştur
    const video = document.createElement('video');
    video.src = `${API_BASE_URL}/files/stream/${file.Rumuz}`;
    video.controls = true;
    video.autoplay = true;
    video.style.width = '100%';
    video.style.height = '100%';
    video.style.backgroundColor = 'black';

    // Tam ekran durumunu dinle
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) {
        // Tam ekrandan çıkıldıysa videoyu durdur ve elementi kaldır
        video.pause();
        if (video.parentNode) {
          video.parentNode.removeChild(video);
        }
      }
    };

    video.addEventListener('fullscreenchange', handleFullscreenChange);
    video.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    video.addEventListener('mozfullscreenchange', handleFullscreenChange);
    video.addEventListener('msfullscreenchange', handleFullscreenChange);

    // Video hata durumunda kontrol et
    video.onerror = async () => {
      await checkVideoStatus(file.Rumuz, videoIndex, false);
    };

    // Body'ye ekle ve tam ekrana geç
    document.body.appendChild(video);
    
    if (video.requestFullscreen) {
      await video.requestFullscreen();
    }
  };

  // Video durumunu kontrol et
  const checkVideoStatus = async (videoRumuz: string, videoIndex: number, showToastMessage: boolean = true) => {
    try {
      const response = await fetch(`${API_BASE_URL}/files/stream/${videoRumuz}`);

      // Video hazırlanıyor durumu - content-type kontrolü
      const contentType = response.headers.get('content-type');
      
      if (contentType && contentType.includes('application/json')) {
        // JSON response - muhtemelen processing durumu
        const data = await response.json();
        
        if (data.status === 'processing') {
          const retryAfter = data.retryAfter || 5;
          
          setVideoProcessingStatus(prev => ({ ...prev, [videoIndex]: true }));
          
          if (showToastMessage) {
            showToast(data.message || VIDEO_PROCESSING_MESSAGE, 'info');
          }

          // Önceki timer'ı temizle
          if (videoRetryTimers.current[videoIndex]) {
            clearTimeout(videoRetryTimers.current[videoIndex]);
          }

          // Belirtilen süre sonra tekrar dene (sessizce)
          videoRetryTimers.current[videoIndex] = setTimeout(() => {
            checkVideoStatus(videoRumuz, videoIndex, false);
          }, retryAfter * 1000);

          return false; // Video hazır değil
        }
      } else if (response.ok && contentType && contentType.includes('video')) {
        // Video hazır - content-type video
        setVideoProcessingStatus(prev => ({ ...prev, [videoIndex]: false }));
        
        // Timer'ı temizle
        if (videoRetryTimers.current[videoIndex]) {
          clearTimeout(videoRetryTimers.current[videoIndex]);
          delete videoRetryTimers.current[videoIndex];
        }

        // Video elementini yeniden yükle
        if (videoRefs.current[videoIndex]) {
          const videoElement = videoRefs.current[videoIndex];
          videoElement!.load();
          
          if (showToastMessage) {
            showToast('Video hazır!', 'success');
          }
        }

        return true; // Video hazır
      }
      
      // Hata durumu
      setVideoProcessingStatus(prev => ({ ...prev, [videoIndex]: false }));
      
      if (showToastMessage) {
        showToast('Video yüklenirken bir hata oluştu.', 'error');
      }
      
      return false;
    } catch (error) {
      console.error('Video durum kontrolü hatası:', error);
      setVideoProcessingStatus(prev => ({ ...prev, [videoIndex]: false }));
      return false;
    }
  };

  // HTML'den temiz metin çıkarma ve boş olup olmadığını kontrol etme
  const hasVisibleText = (htmlString: string | null | undefined): boolean => {
    if (!htmlString) return false;
    
    // HTML taglarını kaldır
    const textContent = htmlString.replace(/<[^>]*>/g, '').trim();
    
    // Boş string, sadece whitespace veya &nbsp; gibi HTML entity'leri kontrol et
    return textContent.length > 0 && textContent !== '&nbsp;';
  };

  // Ek notların varlığını kontrol etme (component'e geçirmek için)
  const hasAdditionalNotes = additionalNotes && additionalNotes.length > 0 && 
    additionalNotes.some(note => note.Notlar && hasVisibleText(note.Notlar));

  // Servis talebi silme fonksiyonları
  const handleDeleteClick = () => {
    setIsDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!serviceDetail?.Id) return;
    
    try {
      setIsDeleting(true);
      const response = await deleteServiceRequest(serviceDetail.Id);
      
      if (response.success) {
        showToast('Servis talebi başarıyla silindi.', 'success');
        
        // Account cache'i güncelle
        notifyAccountCacheDelete('request');
        
        // React Query cache'ini invalidate et - tüm serviceRequests sorgularını yenile
        queryClient.invalidateQueries({ queryKey: ['serviceRequests'] });
        queryClient.invalidateQueries({ queryKey: ['serviceRequestsSearch'] });
        
        setTimeout(() => {
          navigate('/service-requests');
        }, 2000);
      } else {
        showToast('Servis talebi silinirken bir hata oluştu.', 'error');
      }
    } catch (error) {
      console.error('Servis talebi silme hatası:', error);
      showToast('Servis talebi silinirken bir hata oluştu.', 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteConfirmOpen(false);
  };

  // Revize Et düğmesi handler'ı - ServiceRequest sayfasına yönlendir
  const handleReviseClick = () => {
    navigate(`/service-request/${serviceDetail.Id}`);
  };

  // Geri gitme fonksiyonu
  const handleGoBack = () => {
    navigate(-1); // Bir önceki sayfaya git
  };

  // Servis talebinin silinebilir olup olmadığını kontrol et
  const canDeleteServiceRequest = serviceDetail && 
    serviceDetail.IslemAdi === IslemAdi.ONAY_BEKLIYOR &&
    userData?.User === serviceDetail.Cari;
  
  // Servis talebinin revize edilebilir olup olmadığını kontrol et
  const canReviseServiceRequest = serviceDetail && 
    (serviceDetail.IslemAdi === IslemAdi.IPTAL_EDILDI || 
    serviceDetail.SonEtkinlikTipi === EtkinlikTipi.IPTAL_EDILDI) &&
    userData?.User === serviceDetail.Cari;

  // Tarih formatını düzenle
  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    // /Date(1752260040000)/ formatını çıkarıp timestamp elde ediyoruz
    const timestamp = parseInt(dateString.replace(/\/Date\((\d+)\)\//, '$1'), 10);
    if (isNaN(timestamp)) return '-';
    const date = new Date(timestamp);
    return date.toLocaleDateString('tr-TR', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Yedek parça verilerini dönüştür (artık kullanılmıyor - ayrı API çağrısı yapılıyor)
  const transformSparePartsData = (sparePartsData: any[]) => {
    return sparePartsData.map(part => ({
      id: part.YedekParca,
      name: part.Ad,
      code: part.Kod,
      group: part.GrupAdi,
      image: part.Gorsel,
      quantity: part.Miktar,
      laborCost: part.IscilikBedeli
    }));
  };

  // Servis talebi detaylarını yükle
  useEffect(() => {
    const fetchServiceDetail = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        const response = await getServiceRequestDetail(id);
        
        if (response.data && response.data.length > 0) {
          const detail = response.data[0];
          setServiceDetail(detail);
          
          // Yedek parça bilgilerini ayrı endpoint'ten çek
          try {
            const sparePartsResponse = await getServiceRequestSpareParts(id);
            if (sparePartsResponse.data && sparePartsResponse.data.length > 0) {
              const transformedSpareParts = transformSparePartsData(sparePartsResponse.data);
              setSpareParts(transformedSpareParts);
            } else {
              setSpareParts([]);
            }
          } catch (sparePartsError) {
            console.error('Yedek parçalar yüklenirken hata:', sparePartsError);
            setSpareParts([]);
            // Yedek parça yüklenememesi ana işlemi engellemez
          }
          
          // Servis notlarını çek
          try {
            const notesResponse = await getNotesData('ServisNotlari', 'Talepler', id);
            if (notesResponse.success && notesResponse.data) {
              setServiceNotes(notesResponse.data);
            }
          } catch (notesError) {
            console.error('Servis notları yüklenirken hata:', notesError);
            // Servis notları yüklenememesi ana işlemi engellemez
          }

          // Eğer talep durumu "Onay Bekliyor" değilse ek notları da çek
          if (detail.IslemAdi !== IslemAdi.ONAY_BEKLIYOR) {
            try {
              const additionalNotesResponse = await getNotesData('Notlar', 'Talepler', id);
              if (additionalNotesResponse.success && additionalNotesResponse.data) {
                setAdditionalNotes(additionalNotesResponse.data);
              }
            } catch (additionalNotesError) {
              console.error('Ek notlar yüklenirken hata:', additionalNotesError);
              // Ek notların yüklenememesi ana işlemi engellemez
            }
          }

          // Kayıt dosyalarını çek
          try {
            const filesResponse = await getRecordFiles(id, 'Talepler');
            if (filesResponse.success && filesResponse.data) {
              setRecordFiles(filesResponse.data);
            }
          } catch (filesError) {
            console.error('Kayıt dosyaları yüklenirken hata:', filesError);
            // Dosya yüklenememesi ana işlemi engellemez
          }
        }
      } catch (error) {
        console.error('Servis talebi detayları yüklenirken hata:', error);
        showToast('Servis talebi detayları yüklenirken bir hata oluştu.', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchServiceDetail();
  }, [id]);

  // Component unmount olduğunda timer'ları temizle
  useEffect(() => {
    return () => {
      Object.values(videoRetryTimers.current).forEach(timer => {
        clearTimeout(timer);
      });
    };
  }, []);


  // İlk yükleme tamamlandığında buton gösterimini aktifleştir
  useEffect(() => {
    if (!loading && serviceDetail) {
      const timer = setTimeout(() => {
        setIsInitialLoad(false);
      }, 500); // 500ms gecikme
      
      return () => clearTimeout(timer);
    }
  }, [loading, serviceDetail]);

  // Scroll olayını dinle - butonları gizle/göster
  useEffect(() => {
    const handleScroll = () => {
      const st = window.pageYOffset || document.documentElement.scrollTop;
      const isModalOpen = document.body.hasAttribute('data-modal-open');
      
      // Modal açıksa butonları gizle
      if (isModalOpen) {
        setShowDeleteButton(false);
        setShowReviseButton(false);
        setShowBackButton(false);
        return;
      }
      
      // Modal kapandıysa, mevcut scroll pozisyonuna göre butonların durumunu yeniden değerlendir
      if (lastScrollTop.current === undefined || lastScrollTop.current === null) {
        setShowDeleteButton(true);
        setShowReviseButton(true);
        setShowBackButton(true);
        lastScrollTop.current = st;
        return;
      }
      
      // Butonlar için threshold (10px)
      if (st > lastScrollTop.current + 10) {
        setShowDeleteButton(false);
        setShowReviseButton(false);
        setShowBackButton(false);
      } else if (st < lastScrollTop.current - 10) {
        setShowDeleteButton(true);
        setShowReviseButton(true);
        setShowBackButton(true);
      }
      
      // Son scroll pozisyonunu kaydet
      lastScrollTop.current = st <= 0 ? 0 : st;
    };

    // Modal kapanma durumunu özel olarak kontrol eden fonksiyon
    const handleModalStateChange = () => {
      const isModalOpen = document.body.hasAttribute('data-modal-open');
      
      if (!isModalOpen) {
        // Modal kapandı - butonları tekrar göster
        const st = window.pageYOffset || document.documentElement.scrollTop;
        setShowDeleteButton(true);
        setShowReviseButton(true);
        setShowBackButton(true);
        lastScrollTop.current = st;
        
        // Kısa bir delay ile normal scroll davranışını başlat
        setTimeout(() => {
          handleScroll();
        }, 50);
      } else {
        // Modal açıldı - butonları gizle
        setShowDeleteButton(false);
        setShowReviseButton(false);
        setShowBackButton(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    
    // Body attribute değişikliklerini izle (modal açılıp kapanması için)
    const observer = new MutationObserver(handleModalStateChange);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['data-modal-open']
    });
    
    // İlk kontrol
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      observer.disconnect();
    };
  }, []);
  


  // getIssueReasonText fonksiyonu kaldırıldı - artık ArizaNedeniAdi alanı doğrudan kullanılıyor

  // Dosya tipini video olarak algılamak için yardımcı fonksiyon
  const isVideoFile = (file: any): boolean => {
    if (!file) return false;
    if (file.Tip && typeof file.Tip === 'string') {
      const t = file.Tip.toLowerCase();
      if (t.includes('video')) return true;
    }
    const name = (file.Ad || '').toLowerCase();
    return name.endsWith('.mp4') || name.endsWith('.avi') || name.endsWith('.mov') || name.endsWith('.wmv') || name.endsWith('.mkv') || name.endsWith('.webm');
  };

  // Medya görüntüleyici hem görsel hem video dosyalarıyla çalışır
  const mediaFilesForViewer = recordFiles.map(file => ({
    ...file,
    isVideo: isVideoFile(file)
  }));

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!serviceDetail) {
    return (
      <PageTransition>
        <div className="max-w-4xl mx-auto">
          {/* Header Section */}
          <div className="mb-8">
            <div className="flex items-center gap-4">
              <div className="flex flex-col md:flex-row md:items-center md:gap-3">
                <div className="flex items-center gap-3">
                  <div className="hidden md:flex w-12 h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-xl items-center justify-center shadow-lg">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div>
                    <h1 className={`text-2xl font-bold text-gray-900 dark:text-white transition-all duration-300 ease-in-out md:ml-0 ${isMenuButtonVisible ? 'ml-12' : 'ml-0'}`}>
                      Talep Detayları
                    </h1>
                    <p className={`text-sm text-gray-600 dark:text-gray-400 mt-1 transition-all duration-300 ease-in-out md:ml-0 ${isMenuButtonVisible ? 'ml-12' : 'ml-0'}`}>
                      Talep bulunamadı
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content Card */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-xl bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 shadow-lg overflow-hidden">
            <div className="rounded-xl p-6 sm:p-8 border border-white dark:border-gray-700">
              <div className="max-w-md mx-auto">
                {/* Header Icon - Üst bilgi simgesi her durumda gösterilsin */}
                <div className="flex justify-center mb-8">
                  <div className="w-24 h-24 bg-gradient-to-br from-red-100 to-red-200 dark:from-red-900/30 dark:to-red-900/30 rounded-2xl flex items-center justify-center shadow-lg">
                    <svg className="w-12 h-12 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                </div>

                {/* Hata mesajı */}
                <div className="text-center mb-8">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                    Servis Talebi Bulunamadı
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400">
                    Aradığınız servis talebi bulunamadı. Lütfen talep numaranızı kontrol edin.
                  </p>
                </div>

                {/* Ana Sayfaya Dön düğmesi - Modern blue/indigo gradyan */}
                <button
                  onClick={() => navigate('/')}
                  className={`
                    w-full flex items-center justify-center px-6 py-3.5
                    text-base font-semibold text-white
                    bg-gradient-to-r from-blue-600 to-indigo-600
                    hover:from-blue-700 hover:to-indigo-700
                    focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
                    rounded-lg shadow-lg shadow-blue-500/25
                    transform transition-all duration-200
                    hover:scale-[1.02] hover:shadow-xl hover:shadow-blue-500/30
                    active:scale-[0.98]
                  `}
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                  Ana Sayfaya Dön
                </button>
              </div>
            </div>
          </div>
        </div>
      </PageTransition>
    );
  }

  return (
    <PageTransition>
      <div className="max-w-4xl mx-auto">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex items-center gap-4">
            <div className="flex flex-col md:flex-row md:items-center md:gap-3">
              <div className="flex items-center gap-3">
                <div className="hidden md:flex w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl items-center justify-center shadow-lg">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <h1 className={`text-2xl font-bold text-gray-900 dark:text-white transition-all duration-300 ease-in-out md:ml-0 ${isMenuButtonVisible ? 'ml-12' : 'ml-0'}`}>
                    Talep Detayları
                  </h1>
                  <p className={`text-sm text-gray-600 dark:text-gray-400 mt-1 transition-all duration-300 ease-in-out md:ml-0 ${isMenuButtonVisible ? 'ml-12' : 'ml-0'}`}>
                    Servis talebinizin detaylı bilgileri
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Floating Geri Butonu - Sadece mobilde görünür */}
          <AnimatePresence>
            {!loading && !isInitialLoad && showBackButton && !(isMobile && isSidebarOpen) && (
              <motion.div
                className="fixed top-4 right-4 z-50 md:hidden"
                initial={{ scale: 0, opacity: 0, y: -20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0, opacity: 0, y: -20 }}
                transition={{
                  type: "spring",
                  stiffness: 300,
                  damping: 20,
                  duration: 0.4
                }}
              >
                <motion.button
                  onClick={handleGoBack}
                  className="relative bg-gradient-to-r from-slate-500 to-slate-600 hover:from-slate-600 hover:to-slate-700 text-white p-2.5 rounded-full shadow-lg shadow-slate-500/25 hover:shadow-xl hover:shadow-slate-500/30 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500"
                  aria-label="Geri Git"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ type: "spring", stiffness: 400, damping: 17 }}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                  </svg>
                </motion.button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Floating Revize Butonu - Mobilde geri butonunun altında, desktop'ta en üstte */}
          <AnimatePresence>
            {canReviseServiceRequest && !loading && !isInitialLoad && showReviseButton && !(isMobile && isSidebarOpen) && (
              <motion.div
                className="fixed top-16 md:top-4 right-4 z-50"
                initial={{ scale: 0, opacity: 0, y: -20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0, opacity: 0, y: -20 }}
                transition={{
                  type: "spring",
                  stiffness: 300,
                  damping: 20,
                  delay: 0.1
                }}
              >
                <motion.button
                  onClick={handleReviseClick}
                  className="relative bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white p-2.5 rounded-full shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-blue-500/30 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  aria-label="Revize Et"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ type: "spring", stiffness: 400, damping: 17 }}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </motion.button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Floating Silme Butonu - Responsive konumlandırma */}
          <AnimatePresence>
            {canDeleteServiceRequest && !loading && !isInitialLoad && showDeleteButton && !(isMobile && isSidebarOpen) && (
              <motion.div
                className={`fixed ${canReviseServiceRequest ? 'top-28 md:top-16' : 'top-16 md:top-4'} right-4 z-50`}
                initial={{ scale: 0, opacity: 0, y: -20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0, opacity: 0, y: -20 }}
                transition={{
                  type: "spring",
                  stiffness: 300,
                  damping: 20,
                  delay: canReviseServiceRequest ? 0.2 : 0.1
                }}
              >
                <motion.button
                  onClick={handleDeleteClick}
                  disabled={isDeleting}
                  className="relative bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white p-2.5 rounded-full shadow-lg shadow-red-500/25 hover:shadow-xl hover:shadow-red-500/30 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label={isDeleting ? 'Siliniyor...' : 'Talebi Sil'}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ type: "spring", stiffness: 400, damping: 17 }}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>

                  {isDeleting && (
                    <div className="absolute inset-0 flex items-center justify-center bg-red-600 rounded-full">
                      <svg className="animate-spin h-3.5 w-3.5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </div>
                  )}
                </motion.button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Main Content Card */}
        <div className="border border-gray-200 dark:border-gray-700 rounded-xl bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 shadow-lg overflow-hidden">
          <div className="rounded-xl p-6 sm:p-8 border border-white dark:border-gray-700">
            <div className="max-w-md mx-auto">
              {/* Header Icon */}
              <div className="flex justify-center mb-8">
                <div className="w-24 h-24 bg-gradient-to-br from-blue-100 to-indigo-200 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-2xl flex items-center justify-center shadow-lg">
                  <svg className="w-12 h-12 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
              </div>

              {/* Servis Talebi No ve Durum */}
              <div className="text-center mb-8">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                  Talep No: {serviceDetail.No}
                </h2>
                
                <div className="space-y-3">
                  {/* Talep Durumu */}
                  <div className="flex justify-center">
                    {serviceDetail.IslemAdi !== IslemAdi.ONAY_BEKLIYOR && hasAdditionalNotes ? (
                      // Ek notlar varsa tüm etiketi tıklanabilir yap
                      <button
                        onClick={() => setIsNotesModalOpen(true)}
                        className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 hover:scale-105 hover:shadow-md ${
                          serviceDetail.IslemAdi === IslemAdi.ONAYLANDI
                            ? 'bg-gradient-to-br from-green-100 via-emerald-100 to-green-200 text-green-800 dark:from-green-900/30 dark:via-green-900/25 dark:to-green-800/30 dark:text-green-300 hover:from-green-200 hover:via-emerald-200 hover:to-green-300 dark:hover:from-green-900/40 dark:hover:via-green-900/35 dark:hover:to-green-800/40'
                            : serviceDetail.IslemAdi === IslemAdi.IPTAL_EDILDI
                            ? 'bg-gradient-to-br from-red-100 via-rose-100 to-red-200 text-red-800 dark:from-red-900/30 dark:via-red-900/25 dark:to-red-800/30 dark:text-red-300 hover:from-red-200 hover:via-rose-200 hover:to-red-300 dark:hover:from-red-900/40 dark:hover:via-red-900/35 dark:hover:to-red-800/40'
                            : 'bg-gradient-to-br from-blue-100 via-indigo-100 to-blue-200 text-blue-800 dark:from-blue-900/30 dark:via-blue-900/25 dark:to-blue-800/30 dark:text-blue-300 hover:from-blue-200 hover:via-indigo-200 hover:to-blue-300 dark:hover:from-blue-900/40 dark:hover:via-blue-900/35 dark:hover:to-blue-800/40'
                        }`}
                      >
                        {serviceDetail.IslemAdi}
                        <svg className="w-5 h-5 ml-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9 9a1 1 0 012 0v3a1 1 0 11-2 0V9zm1-4a1 1 0 100 2 1 1 0 000-2z" clipRule="evenodd" />
                        </svg>
                      </button>
                    ) : (
                      // Ek notlar yoksa normal etiket
                      <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium ${
                        serviceDetail.IslemAdi === IslemAdi.ONAY_BEKLIYOR 
                          ? 'bg-gradient-to-br from-blue-100 via-indigo-100 to-blue-200 text-blue-800 dark:from-blue-900/30 dark:via-blue-900/25 dark:to-blue-800/30 dark:text-blue-300'
                          : serviceDetail.IslemAdi === IslemAdi.ONAYLANDI
                          ? 'bg-gradient-to-br from-green-100 via-emerald-100 to-green-200 text-green-800 dark:from-green-900/30 dark:via-green-900/25 dark:to-green-800/30 dark:text-green-300'
                          : serviceDetail.IslemAdi === IslemAdi.IPTAL_EDILDI
                          ? 'bg-gradient-to-br from-red-100 via-rose-100 to-red-200 text-red-800 dark:from-red-900/30 dark:via-red-900/25 dark:to-red-800/30 dark:text-red-300'
                          : 'bg-gradient-to-br from-blue-100 via-indigo-100 to-blue-200 text-blue-800 dark:from-blue-900/30 dark:via-blue-900/25 dark:to-blue-800/30 dark:text-blue-300'
                      }`}>
                        {serviceDetail.IslemAdi}
                      </span>
                    )}
                  </div>
                  
                  {/* Talep Tipi */}
                  <div className="flex justify-center">
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                      serviceDetail.TipAdi === 'Alıcı Talebi'
                        ? 'bg-gradient-to-br from-indigo-100 via-purple-100 to-indigo-200 text-indigo-800 dark:from-indigo-900/30 dark:via-indigo-900/25 dark:to-indigo-800/30 dark:text-indigo-300'
                        : serviceDetail.TipAdi === 'Satıcı Talebi'
                        ? 'bg-gradient-to-br from-amber-100 via-yellow-100 to-amber-200 text-amber-800 dark:from-amber-900/30 dark:via-amber-900/25 dark:to-amber-800/30 dark:text-amber-300'
                        : 'bg-gradient-to-br from-gray-100 via-slate-100 to-gray-200 text-gray-800 dark:from-gray-900/30 dark:via-gray-900/25 dark:to-gray-800/30 dark:text-gray-300'
                    }`}>
                      {serviceDetail.TipAdi}
                    </span>
                  </div>

                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Son Etkinlik Bilgisi */}
        {serviceDetail.SonEtkinlik && (
          <div className={`${
            serviceDetail.SonEtkinlikTipi === EtkinlikTipi.IPTAL_EDILDI
              ? 'bg-gradient-to-br from-red-50 via-rose-50 to-red-100 dark:from-red-900/20 dark:via-red-900/15 dark:to-red-800/20 border border-red-200 dark:border-red-800'
              : 'bg-gradient-to-br from-amber-50 via-yellow-50 to-amber-100 dark:from-amber-900/20 dark:via-amber-900/15 dark:to-amber-800/20 border border-amber-200 dark:border-amber-800'
          } rounded-lg mt-6`}>
            <div className="rounded-lg p-3 border border-white dark:border-gray-700">
              <div className="flex items-center">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center mr-3 flex-shrink-0 ${
                  serviceDetail.SonEtkinlikTipi === EtkinlikTipi.IPTAL_EDILDI
                    ? 'bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-700'
                    : 'bg-amber-100 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700'
                }`}>
                  <svg className={`w-3.5 h-3.5 ${
                    serviceDetail.SonEtkinlikTipi === EtkinlikTipi.IPTAL_EDILDI
                      ? 'text-red-600 dark:text-red-400'
                      : 'text-amber-600 dark:text-amber-400'
                  }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <p className={`text-xs font-medium tracking-wide ${
                    serviceDetail.SonEtkinlikTipi === EtkinlikTipi.IPTAL_EDILDI
                      ? 'text-red-700 dark:text-red-300'
                      : 'text-amber-700 dark:text-amber-300'
                  }`}>SON ETKİNLİK</p>
                  <p className={`text-sm font-medium ${
                    serviceDetail.SonEtkinlikTipi === EtkinlikTipi.IPTAL_EDILDI
                      ? 'text-red-800 dark:text-red-200'
                      : 'text-amber-800 dark:text-amber-200'
                  }`}>{serviceDetail.SonEtkinlik}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Ürün Bilgileri */}
        <div className="border border-gray-200 dark:border-gray-700 rounded-xl bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 shadow-lg mt-6">
          <div className="rounded-xl p-6 sm:p-8 border border-white dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Ürün Bilgileri
          </h3>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 lg:space-x-4">
            {/* Ürün Özellikleri */}
            <div className="space-y-4">
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 dark:text-white mb-3">Ürün Özellikleri</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Kategori:</span>
                    <span className="font-medium text-gray-900 dark:text-white">{serviceDetail.KategoriAdi}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Marka:</span>
                    <span className="font-medium text-gray-900 dark:text-white">{serviceDetail.MarkaAdi}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Model:</span>
                    <span className="font-medium text-gray-900 dark:text-white">{serviceDetail.ModelAdi}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Renk:</span>
                    <span className="font-medium text-gray-900 dark:text-white">{serviceDetail.RenkAdi}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Seri No:</span>
                    <span className="font-medium text-gray-900 dark:text-white font-mono text-xs">{serviceDetail.Sasi}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Motor No:</span>
                    <span className="font-medium text-gray-900 dark:text-white font-mono text-xs">{serviceDetail.Motor}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Ayraç */}
            <div className="border-t border-gray-200 dark:border-gray-600 my-4 lg:hidden"></div>

            {/* Talep Bilgileri */}
            <div className="space-y-4">
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 dark:text-white mb-3">Talep Bilgileri</h4>
                <div className="space-y-2 text-sm">
                  {userData?.KmIsRequired && (
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Kilometre:</span>
                      <span className="font-medium text-gray-900 dark:text-white">{serviceDetail.Km?.toLocaleString('tr-TR')} km</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Arıza Nedeni:</span>
                    <span className="font-medium text-gray-900 dark:text-white">{serviceDetail.ArizaNedeniAdi}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Talep Tarihi:</span>
                    <span className="font-medium text-gray-900 dark:text-white">{formatDate(serviceDetail.Tarih)}</span>
                  </div>
                  {serviceDetail.IslemTarihi && (
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">İşlem Tarihi:</span>
                      <span className="font-medium text-gray-900 dark:text-white">{formatDate(serviceDetail.IslemTarihi)}</span>
                    </div>
                  )}
                  {serviceDetail.FaturaNo && (
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Fatura No:</span>
                      <span className="font-medium text-gray-900 dark:text-white font-mono text-xs">{serviceDetail.FaturaNo}</span>
                    </div>
                  )}
                </div>
              </div>


            </div>
          </div>
          </div>
        </div>

        {/* Kullanıcı Bilgileri */}
        <div className="border border-gray-200 dark:border-gray-700 rounded-xl bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 shadow-lg mt-6">
          <div className="rounded-xl p-6 sm:p-8 border border-white dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Kullanıcı Bilgileri
          </h3>
          
          <div className={`grid grid-cols-1 ${serviceDetail.TipAdi === 'Satıcı Talebi' ? 'lg:grid-cols-1' : 'lg:grid-cols-2'} gap-6`}>
            {/* Satıcı Bilgileri */}
            <div className="border border-indigo-100 dark:border-gray-600 rounded-lg bg-gradient-to-br from-indigo-50 via-blue-50 to-indigo-100 dark:from-gray-700 dark:via-gray-700/80 dark:to-gray-600">
              <div className="rounded-lg p-4 border border-white dark:border-gray-700">
              <h4 className="font-medium text-blue-900 dark:text-blue-200 mb-3 flex items-center">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                Satıcı Bilgileri
              </h4>
              <div className="space-y-2 text-sm">
                <div>
                  <span className="text-blue-700 dark:text-blue-300 font-medium">{serviceDetail.SaticiAdi}</span>
                </div>
                <div>
                  <span className="text-blue-600 dark:text-blue-400">
                    {isValidTurkishMobile(serviceDetail.SaticiTelefon) 
                      ? formatPhoneNumber(serviceDetail.SaticiTelefon)
                      : serviceDetail.SaticiTelefon
                    }
                  </span>
                </div>
              </div>
              </div>
            </div>

            {/* Alıcı Bilgileri - Sadece Alıcı Talebi durumunda göster */}
            {serviceDetail.TipAdi !== 'Satıcı Talebi' && (
              <div className="border border-indigo-100 dark:border-gray-600 rounded-lg bg-gradient-to-br from-indigo-50 via-blue-50 to-indigo-100 dark:from-gray-700 dark:via-gray-700/80 dark:to-gray-600">
                <div className="rounded-lg p-4 border border-white dark:border-gray-700">
                <h4 className="font-medium text-green-900 dark:text-green-200 mb-3 flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  Müşteri Bilgileri
                </h4>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="text-green-700 dark:text-green-300 font-medium">{serviceDetail.AliciAdi}</span>
                  </div>
                  <div>
                    <span className="text-green-600 dark:text-green-400">
                      {isValidTurkishMobile(serviceDetail.AliciTelefon) 
                        ? formatPhoneNumber(serviceDetail.AliciTelefon)
                        : serviceDetail.AliciTelefon
                      }
                    </span>
                  </div>
                </div>
                </div>
              </div>
            )}
          </div>
          </div>
        </div>

        {/* Yedek Parça Bilgileri */}
        {spareParts.length > 0 && (
          <div className="border border-gray-200 dark:border-gray-700 rounded-xl bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 shadow-lg mt-6">
            <div className="rounded-xl p-6 sm:p-8 border border-white dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Talep Edilen Parçalar ({spareParts.length} adet)
            </h3>
            
            <div className="space-y-4">
              {spareParts.map((part, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="border border-gray-200 dark:border-gray-600 rounded-lg bg-gradient-to-br from-gray-50 via-slate-50 to-gray-100 dark:from-gray-700 dark:via-gray-700/90 dark:to-gray-600"
                >
                  <div className="rounded-lg p-4 border border-white dark:border-gray-700 flex items-start space-x-4">
                  {/* Parça Görseli */}
                  <div className="flex-shrink-0">
                    <ProductImage
                      imageId={part.image}
                      alt={part.name}
                      size="w-16 h-16"
                      iconSize="w-8 h-8"
                    />
                  </div>

                  {/* Parça Bilgileri */}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:justify-between">
                      <div className="mb-2 sm:mb-0">
                        <h4 className="text-sm font-medium text-gray-900 dark:text-white break-words">
                          {part.name}
                        </h4>
                        <p className="text-xs text-gray-500 dark:text-gray-400 font-mono break-all">
                          {part.code}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 break-words">
                          {part.group}
                        </p>
                      </div>

                      {/* Miktar */}
                      <div className="text-right">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          Miktar: {part.quantity}
                        </div>
                        {part.laborCost > 0 && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            İşçilik: ₺{part.laborCost.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  </div>
                </motion.div>
              ))}
            </div>
            </div>
          </div>
        )}

        {/* Talep Notları */}
        {serviceNotes.length > 0 && serviceNotes.some(note => hasVisibleText(note.Notlar)) && (
          <div className="border border-gray-200 dark:border-gray-700 rounded-xl bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 shadow-lg mt-6">
            <div className="rounded-xl p-6 sm:p-8 border border-white dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Talep Notları
            </h3>
            
            <div className="space-y-3">
              {serviceNotes
                .filter(note => hasVisibleText(note.Notlar))
                .map((note, index) => (
                  <div key={index} className="border border-indigo-100 dark:border-gray-600 rounded bg-gradient-to-br from-indigo-50 via-blue-50 to-indigo-100 dark:from-gray-700 dark:via-gray-700/80 dark:to-gray-600">
                    <div className="rounded p-3 border border-white dark:border-gray-700">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                          <svg className="h-3 w-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9 9a1 1 0 012 0v3a1 1 0 11-2 0V9zm1-4a1 1 0 100 2 1 1 0 000-2z" clipRule="evenodd" />
                        </svg>
                        </div>
                      </div>
                      <div className="ml-3">
                        <p className="text-xs font-medium text-blue-800 dark:text-blue-200 mb-1">
                          Talep Notu
                        </p>
                        <div 
                          className="text-sm text-blue-700 dark:text-blue-300"
                          dangerouslySetInnerHTML={{ __html: note.Notlar }}
                        />
                      </div>
                    </div>
                    </div>
                  </div>
                ))}
            </div>
            </div>
          </div>
        )}

        {/* Talep Görselleri / Videoları */}
        {recordFiles.length > 0 && (
          <div className="border border-gray-200 dark:border-gray-700 rounded-xl bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 shadow-lg mt-6">
            <div className="rounded-xl p-6 sm:p-8 border border-white dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Talep Medyaları
            </h3>
            
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {recordFiles.map((file, index) => {
                const isVideo = isVideoFile(file);
                  if (isVideo) {
                    // Video kartı
                    return (
                      <div key={index} className="relative group">
                        <div className="relative w-full h-32 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 hover:border-indigo-500 transition-colors">
                        <img
                          src={`${API_BASE_URL}/files/read/${file.Rumuz}`}
                          alt={file.Ad}
                          className="w-full h-full object-cover cursor-pointer"
                          onClick={() => {
                            // Video da modalda açılabilir
                            const mediaIndex = mediaFilesForViewer.findIndex(media => media.Rumuz === file.Rumuz);
                            if (mediaIndex >= 0) {
                              setCurrentImageIndex(mediaIndex);
                              setIsImageViewerOpen(true);
                            }
                          }}
                        />
                        <div className={`absolute inset-0 ${videoProcessingStatus[index] ? 'bg-black/40' : 'bg-black/0 group-hover:bg-black/20'} transition-colors rounded-lg pointer-events-none flex items-center justify-center`}>
                          {videoProcessingStatus[index] ? (
                            // Processing durumunda spinner göster
                            <div className="flex flex-col items-center justify-center">
                              <svg className="animate-spin h-10 w-10 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 004 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              <span className="text-white text-xs mt-2 font-medium">Hazırlanıyor...</span>
                            </div>
                            ) : (
                            // Normal durumda play ikonu göster (sadece desktop'ta)
                            <div className="hidden md:flex w-12 h-12 bg-black bg-opacity-50 rounded-full items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                              <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M8 5v14l11-7z"/>
                              </svg>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-2 rounded-b-lg pointer-events-none truncate">
                        {file.Ad}
                      </div>
                    </div>
                  );
                }

                // Görsel kartı
                return (
                  <div key={index} className="relative group">
                    <img
                      src={`${API_BASE_URL}/files/read/${file.Rumuz}`}
                      alt={file.Ad}
                      className="w-full h-32 object-cover rounded-lg border border-gray-200 dark:border-gray-700 hover:border-indigo-500 transition-colors cursor-pointer"
                      onClick={() => {
                        // Tüm medya dosyaları modalda açılabilir: index'i medya listesine göre ayarla
                        const mediaIndex = mediaFilesForViewer.findIndex(media => media.Rumuz === file.Rumuz);
                        if (mediaIndex >= 0) {
                          setCurrentImageIndex(mediaIndex);
                          setIsImageViewerOpen(true);
                        }
                      }}
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-opacity rounded-lg flex items-center justify-center pointer-events-none">
                      <svg className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-2 rounded-b-lg pointer-events-none truncate">
                      {file.Ad}
                    </div>
                  </div>
                );
              })}
            </div>
            </div>
          </div>
        )}
      </div>

      {/* Silme Onay Dialogu */}
      <ConfirmationDialog
        isOpen={isDeleteConfirmOpen}
        title="Servis Talebini Sil"
        message="Bu talebi silmek istediğinizden emin misiniz? Bu işlem geri alınamaz."
        confirmText="Sil"
        cancelText="İptal"
        autoClose={true}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />

      {/* Ek Notlar Modal */}
      <AdditionalNotesModal
        isOpen={isNotesModalOpen}
        onClose={() => setIsNotesModalOpen(false)}
        notes={additionalNotes}
        title="Onay Ayrıntısı"
      />

      {/* Toast */}
      <Toast
        isVisible={toast.isVisible}
        message={toast.message}
        type={toast.type}
        onClose={hideToast}
      />

      {/* Medya Görüntüleyici */}
      <ImageViewerModal
        isOpen={isImageViewerOpen}
        onClose={() => setIsImageViewerOpen(false)}
        images={mediaFilesForViewer}
        currentIndex={currentImageIndex}
      />
    </PageTransition>
  );
} 